/**
 * Usage Command
 * Show subscription tier, credits balance, and 5h/7d rolling-window usage.
 */

import { Command } from 'commander';
import { isAuthenticated } from '../utils/config.js';
import { getCreditsStatus, getUsageLimits } from '../api/credits.js';
import * as output from '../utils/output.js';

function requireAuth(): void {
  if (!isAuthenticated()) {
    output.error('Not logged in. Run `paean login` first.');
    process.exit(1);
  }
}

function renderBar(percent: number, width = 24): string {
  const p = Math.max(0, Math.min(100, percent));
  const filled = Math.round((p / 100) * width);
  const empty = width - filled;
  const bar = '█'.repeat(filled) + '░'.repeat(empty);
  if (p >= 90) return output.colors.error(bar);
  if (p >= 70) return output.colors.warning(bar);
  return output.colors.success(bar);
}

export const usageCommand = new Command('usage')
  .description('Show subscription tier, credits, and rolling-window usage (5h / 7d)')
  .option('--json', 'Output in JSON format')
  .action(async (options) => {
    requireAuth();

    const spin = output.spinner('Fetching usage…').start();

    try {
      const [statusRes, limitsRes] = await Promise.all([
        getCreditsStatus(),
        getUsageLimits(),
      ]);
      spin.stop();

      const status = statusRes.data;
      const limits = limitsRes.data;

      if (options.json) {
        output.json({ status, limits });
        return;
      }

      output.header('Usage');
      output.tableRow('Subscription', output.colors.bold(status.subscriptionTier));
      output.tableRow('Credits', `${status.credits} / ${status.totalCredits}`);
      if (status.canRecover) {
        output.tableRow('Recovery', output.colors.success('available now'));
      } else if (status.nextRecoveryAt) {
        output.tableRow('Next recovery', output.formatDate(status.nextRecoveryAt));
      }

      output.newline();
      output.header('Rolling Window Usage');

      const bar5h = renderBar(limits.percentUsed5h);
      const bar7d = renderBar(limits.percentUsed7d);

      output.tableRow(
        '5h',
        `${bar5h} ${limits.percentUsed5h}%  (${limits.usage5h} / ${limits.limits.credits5h})`,
      );
      output.tableRow(
        '7d',
        `${bar7d} ${limits.percentUsed7d}%  (${limits.usage7d} / ${limits.limits.credits7d})`,
      );

      output.newline();

      if (limits.percentUsed5h >= 90 || limits.percentUsed7d >= 90) {
        output.warning('Approaching usage limits — requests may be throttled.');
      } else if (status.credits < 20) {
        output.warning(
          `Credits are low (${status.credits}). Use \`paean credits deposit-info\` to top up.`,
        );
      }
    } catch (err: any) {
      spin.stop();
      output.errorWithHint(err.message || 'Failed to fetch usage');
    }
  });
